import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { StorePart } from '../../../../types/storePart'
import useItemHistory from './useItemHistory'
import MovementHistoryGrid from './MovementHistoryGrid'

export type MovementHistorySectionProps = {
	storePart: StorePart,
}

/**
 * "How did the quantity get to what it is now, at this one location?" — a
 * reverse-chronological ledger of every transaction that moved stock, plus the cancelled
 * ones that did not. Committed and incoming units live in the state panel above.
 */
export default function MovementHistorySection( { storePart }: MovementHistorySectionProps ) {
	const { entries, totalCount, loading } = useItemHistory( String( storePart.id ) )
	const locationName = storePart.inventoryLocation.name
	// Real histories can exceed the fetched window; be explicit rather than implying full coverage.
	const isWindowed = !loading && totalCount > entries.length
	const countLabel = isWindowed
		? `most recent ${entries.length.toLocaleString( 'en-US' )} of ${totalCount.toLocaleString( 'en-US' )} transactions`
		: `${totalCount.toLocaleString( 'en-US' )} Transactions`

	return (
		<Box component="section">
			<Stack direction="row" alignItems="baseline" justifyContent="space-between" flexWrap="wrap" gap={1} mb={1.5}>
				<Box>
					<Typography variant="h6">Transaction History</Typography>
					<Typography variant="body2" color="text.secondary">
						Transactions at <Box component="strong" fontWeight={700}>{locationName}</Box>. Cancelled transactions are shown but never changed the quantity. Committed and incoming units are shown above.
					</Typography>
				</Box>
				<Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
					{countLabel}
				</Typography>
			</Stack>
			<MovementHistoryGrid
				entries={entries}
				loading={loading}
			/>
		</Box>
	)
}
